if (!customElements.get('pack-picker')) {
  customElements.define(
    'pack-picker',
    class PackPicker extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.onPackClick.bind(this));
        this.hideNativePicker();

        // Sync visual state with the currently selected variant on load
        this.syncFromNative();

        // Keep in sync when the native variant-selects fires a change
        // (e.g. product-info.js updating via URL param on page load)
        this.nativeUnsubscribe = subscribe?.(
          PUB_SUB_EVENTS?.variantChange,
          this.onVariantChange.bind(this)
        );

        // Listen for Skio plan picker changes to update price-per-cup
        this._onSkioChange = this._onSkioChange.bind(this);
        this._onProductUpdated = () => {
          // After a flavor swap, Skio re-inits — wait for it then sync prices
          setTimeout(() => this._syncPriceMode(), 600);
        };
        document.addEventListener('change', this._onSkioChange);
        document.addEventListener('lasso:product-info-updated', this._onProductUpdated);

        // Set initial price mode based on which Skio radio is checked
        this._syncPriceMode();
      }

      disconnectedCallback() {
        this.nativeUnsubscribe?.();
        document.removeEventListener('change', this._onSkioChange);
        document.removeEventListener('lasso:product-info-updated', this._onProductUpdated);
      }

      // ── Hide the native variant picker ──────────────────────────────────────
      hideNativePicker() {
        const container = this.closest('.product__info-container');
        if (!container) return;

        // Only hide the native variant picker wrapper — identified by containing
        // a <variant-selects> element. This avoids hiding our <flavor-picker>
        // which also carries the product-variant-picker-block class.
        container.querySelectorAll('.product-variant-picker-block').forEach((el) => {
          if (el.querySelector('variant-selects')) {
            el.hidden = true;
          }
        });
      }

      // ── Read selected state from the native variant-selects ─────────────────
      syncFromNative() {
        const sectionId = this.dataset.section;
        const checkedInput = document
          .querySelector(`variant-selects[data-section="${sectionId}"]`)
          ?.querySelector('input[type="radio"]:checked');

        if (checkedInput) {
          this.setActiveByOptionValue(checkedInput.value, false);
        }
      }

      // ── Handle variantChange pub-sub event (triggered by product-info.js) ───
      onVariantChange({ data: { sectionId, variant } = {} } = {}) {
        if (sectionId !== this.dataset.section) return;
        if (!variant) return;

        // Find the pill that matches the variant id
        const pill = this.querySelector(`[data-variant-id="${variant.id}"]`);
        if (pill) this.setActivePill(pill, false);
      }

      // ── Click handler ────────────────────────────────────────────────────────
      onPackClick(event) {
        const pill = event.target.closest('.pack-pill');
        if (!pill || pill.disabled || pill.getAttribute('aria-current') === 'true') return;

        this.setActivePill(pill, true);
      }

      setActivePill(pill, triggerNative) {
        // Update visual state
        this.querySelectorAll('.pack-pill').forEach((b) => {
          b.setAttribute('aria-current', 'false');
          b.classList.remove('pack-pill--selected');
        });
        pill.setAttribute('aria-current', 'true');
        pill.classList.add('pack-pill--selected');

        if (triggerNative) {
          this.triggerNativeVariantChange(pill.dataset.optionValue);
        }
      }

      setActiveByOptionValue(optionValue, triggerNative) {
        const pill = this.querySelector(`[data-option-value="${optionValue}"]`);
        if (pill) this.setActivePill(pill, triggerNative);
      }

      // ── Skio plan picker integration ──────────────────────────────────────────
      // When the user toggles between Subscribe and One-Time, update the
      // displayed price-per-cup on every pack pill.

      _onSkioChange(event) {
        const input = event.target;
        if (!input.matches?.('input.skio-group-input[type="radio"]')) return;
        // Only react to Skio radios within the same product section
        const sectionEl = input.closest(`[id*="${this.dataset.section}"]`);
        if (!sectionEl) return;

        const isSubscribe = input.hasAttribute('skio-selling-plan-group');
        this._updatePrices(isSubscribe);
      }

      _syncPriceMode() {
        const sectionId = this.dataset.section;
        const skioContainer = document.querySelector(
          `[id*="${sectionId}"] fieldset.skio-plan-picker`
        );
        if (!skioContainer) return;

        const checked = skioContainer.querySelector('input.skio-group-input[type="radio"]:checked');
        if (!checked) return;
        this._updatePrices(checked.hasAttribute('skio-selling-plan-group'));
      }

      _updatePrices(isSubscribe) {
        const priceKey = isSubscribe ? 'subPrice' : 'otpPrice';

        this.querySelectorAll('.pack-pill[data-otp-price]').forEach((pill) => {
          const priceEl = pill.querySelector('[data-price-display]');
          if (!priceEl) return;

          const cents = Number(pill.dataset[priceKey]);
          if (isNaN(cents)) return;

          // Format using Shopify's money format (cents → dollars)
          priceEl.textContent = this._formatMoney(cents) + ' per cup';
        });
      }

      _formatMoney(cents) {
        // Use Shopify.formatMoney if available, otherwise basic USD format
        if (window.Shopify?.formatMoney) {
          return window.Shopify.formatMoney(cents);
        }
        return '$' + (cents / 100).toFixed(2);
      }

      // ── Trigger the native variant-selects mechanism ─────────────────────────
      // Clicks the corresponding hidden radio input so product-info.js handles
      // price, inventory, Skio, and all other downstream updates automatically.
      triggerNativeVariantChange(optionValue) {
        const sectionId = this.dataset.section;
        const variantSelects = document.querySelector(
          `variant-selects[data-section="${sectionId}"]`
        );
        if (!variantSelects) return;

        const radio = variantSelects.querySelector(
          `input[type="radio"][value="${CSS.escape(optionValue)}"]`
        );
        if (radio && !radio.checked) {
          radio.click();
        }
      }
    }
  );
}


// Patch prototype in case an older inline version was registered first (defer ordering fix)
(function() {
  const PackPickerClass = customElements.get('pack-picker');
  if (!PackPickerClass) return;
  const proto = PackPickerClass.prototype;
  if (proto._syncPriceMode) return; // already has new methods, nothing to do

  proto._onSkioChange = function(event) {
    const input = event.target;
    if (!input.matches || !input.matches('input.skio-group-input[type="radio"]')) return;
    if (!input.closest('[id*="' + this.dataset.section + '"]')) return;
    const isSubscribe = input.hasAttribute('skio-selling-plan-group');
    this._updatePrices(isSubscribe);
  };

  proto._syncPriceMode = function() {
    const sectionId = this.dataset.section;
    const skioContainer = document.querySelector('[id*="' + sectionId + '"] fieldset.skio-plan-picker');
    if (!skioContainer) return;
    const checked = skioContainer.querySelector('input.skio-group-input[type="radio"]:checked');
    if (checked) this._updatePrices(checked.hasAttribute('skio-selling-plan-group'));
  };

  proto._updatePrices = function(isSubscribe) {
    const priceKey = isSubscribe ? 'subPrice' : 'otpPrice';
    var self = this;
    this.querySelectorAll('.pack-pill[data-otp-price]').forEach(function(pill) {
      const priceEl = pill.querySelector('[data-price-display]');
      if (!priceEl) return;
      const cents = Number(pill.dataset[priceKey]);
      if (isNaN(cents)) return;
      priceEl.textContent = self._formatMoney(cents) + ' per cup';
    });
  };

  proto._formatMoney = function(cents) {
    return window.Shopify && window.Shopify.formatMoney ? window.Shopify.formatMoney(cents) : '$' + (cents / 100).toFixed(2);
  };

  function patchPackPicker(el) {
    if (el._ppPatched) return;
    el._ppPatched = true;
    if (el._onSkioChange) {
      document.removeEventListener('change', el._onSkioChange);
    }
    el._onSkioChange = proto._onSkioChange.bind(el);
    document.addEventListener('change', el._onSkioChange);
    if (el._onProductUpdated) {
      document.removeEventListener('lasso:product-info-updated', el._onProductUpdated);
    }
    el._onProductUpdated = function() {
      setTimeout(function() { el._syncPriceMode(); }, 600);
    };
    document.addEventListener('lasso:product-info-updated', el._onProductUpdated);
    el._syncPriceMode();
  }

  // Patch all existing pack-picker elements
  document.querySelectorAll('pack-picker').forEach(patchPackPicker);

  // Watch for new pack-picker elements added to DOM (e.g. after flavor swap)
  var observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType !== 1) return;
        if (node.tagName === 'PACK-PICKER') {
          setTimeout(function() { patchPackPicker(node); }, 0);
        }
        // Also check descendants
        node.querySelectorAll && node.querySelectorAll('pack-picker').forEach(function(pp) {
          setTimeout(function() { patchPackPicker(pp); }, 0);
        });
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();